import React, { useContext, useEffect } from "react";
import { Box, List, ListItem, ListItemIcon, Tooltip } from "@mui/material";

import { ThemeContext } from "../../context/ThemeContext";
import { VideoPlayerContext } from "../../context/VideoPlayerContext";
import { getColors } from "../../themes/theme";
import { useNavigate, useParams } from "react-router-dom";

import { ReactComponent as Pencil } from "../../assets/actionbar/pencil.svg";
import { ReactComponent as Erase } from "../../assets/actionbar/erase.svg";
import { ReactComponent as Exercise } from "../../assets/actionbar/exercise_black.svg";
import { ReactComponent as Care } from "../../assets/actionbar/care_black.svg";
import { ReactComponent as User } from "../../assets/actionbar/user_black.svg";
import { ReactComponent as Share } from "../../assets/actionbar/share.svg";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import axios from "axios";
import {
  brainVideoData,
  spineVideoData,
  shoulderVideoData,
  kneeVideoData,
  ankleNfootVideoData,
  elbowNwristVideoData,
  hipVideoData,
  jawVideoData,
  phaseOfCareVideoData,
  treatmentsVideoData,
  nervousSystemVideoData,
} from "../../utils/videoData";

const actionItems = [
  { icon: <Pencil />, text: "Draw", tool: "draw" },
  { icon: <Erase />, text: "Erase", tool: "erase" },
  { icon: <Exercise />, text: "Exercises", tool: "exercise" },
  { icon: <Care />, text: "Care Plan", tool: "care-plan" },
  { icon: <User />, text: "Users", tool: "users" },
  { icon: <Share />, text: "Share", tool: "share" },
];

export const VideoToolbar = ({ activeTool, onActionSelect, requestClear }) => {
  const navigate = useNavigate();
  const { user, token } = useAuthContext();
  const isUser = user?.securityType === 3;
  const { section, videoId, selectedSubItem } = useParams();
  const { isLoading } = useContext(VideoPlayerContext);
  const { darkMode } = useContext(ThemeContext);

  const videoUrl = `/videos/${videoId}-${selectedSubItem}.mp4`;

  const videoDataMap = {
    brain: brainVideoData,
    spine: spineVideoData,
    shoulder: shoulderVideoData,
    knee: kneeVideoData,
    ankleNfoot: ankleNfootVideoData,
    elbowNwrist: elbowNwristVideoData,
    hip: hipVideoData,
    jaw: jawVideoData,
    nervous_system: nervousSystemVideoData,
    treatments: treatmentsVideoData,
    phase_of_care: phaseOfCareVideoData,
  };

  const videoDetails = videoDataMap[section]?.find(
    (video) => video.routing === `/video/${videoId}`
  );

  useEffect(() => {
    onActionSelect(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl]);

  const handleItemClick = async (tool) => {
    if (tool === activeTool) {
      onActionSelect(null);
      return;
    } else if (tool === "draw") {
      onActionSelect(tool);
    } else if (tool === "erase") {
      requestClear();
    } else if (tool === "exercise") {
      const exerciseData = videoDetails.subItems[selectedSubItem];

      if (exerciseData) {
        if (
          exerciseData &&
          Object.keys(exerciseData).length === 1 && // Check if it has only 1 key
          Array.isArray(Object.values(exerciseData)[0]) && // Check if the value is an array
          Object.values(exerciseData)[0].length === 1 // Check if the array has only 1 item
        ) {
          const key = Object.keys(exerciseData)[0]; // Get the single key
          const item = exerciseData[key][0]; // Get the single item in the array
          // Perform navigation or other actions
          navigate(`/exercise/${key}/${item}`, { state: { exerciseData } });
        } else navigate("/exercise", { state: { exerciseData } });
      } else {
        console.error("No exercise data found for the selected item.");
      }
      onActionSelect(null);
    } else if (tool === "care-plan") {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/condition/getConditionList`,
        {
          careplanData: videoDetails.subItems[selectedSubItem],
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const careplanData = response.data;
      if (careplanData) {
        navigate("/care-plan", { state: { careplanData } });
      } else {
        console.error("No careplan data found for the selected item.");
      }
      onActionSelect(null);
    } else {
      navigate(`/${tool}`);
      onActionSelect(null);
    }
  };

  return (
    <Box
      display={isLoading ? "none" : "flex"}
      flexDirection="column"
      justifyContent="center"
      sx={{
        border: darkMode ? "none" : `1px solid ${getColors.appBarBorderLight}`,
      }}
    >
      <List>
        {actionItems
          .filter((item) => !(isUser && item.tool === "users"))
          .map((item, index) => (
            <Tooltip key={index} title={item.text} arrow placement="right">
              <ListItem
                onClick={() => handleItemClick(item.tool)}
                sx={{
                  background:
                    activeTool === item.tool
                      ? getColors.listItemHoverBg
                      : "inherit",
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: "20px",
                    padding: { xs: "4px", sm: 2 },
                    borderBottom: `1px solid ${getColors.listItemIconBorderBottom}`,
                    "&.MuiListItemIcon-root": {
                      "& svg": {
                        filter:
                          (darkMode || activeTool === item.tool) &&
                          "brightness(0) invert(1)",
                      },
                    },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
              </ListItem>
            </Tooltip>
          ))}
      </List>
    </Box>
  );
};
